import { Form, Button, InputGroup, Accordion } from 'react-bootstrap'
import React, { useEffect, useState, forwardRef, useImperativeHandle, useMemo } from 'react'
import { getRequestHeaders, useActionFetch } from '@/utils/requests'
import QuestionAnswer from '@/types/QuestionAnswer'
import Project from '@/types/Project'
import Portal from '@/types/Portal'
import { useForm, useController } from 'react-hook-form'
import { useToaster } from '@/components/shared/ToasterProvider'
import LoadingButton from '@/components/shared/LoadingButton'
import { getOrCreateSessionToken } from '@/utils/qaSession'
import TitledRecordsSelect from '@/components/TitledRecordsSelect'
import { trackQuestionSubmittedEvent } from '@/utils/analytics'
import ElementWithTooltip from '@/components/shared/ElementWithTooltip'
import AccordionToggleAllButton from '@/components/shared/AccordionToggleAllButton'
import QuestionAnswerAccordionItem from '@/components/QuestionAnswerAccordionItem'
import useAccordionExpand from '@/hooks/useAccordionExpand'
import { apiQuestionAnswers } from '@/routes'

interface Props {
  project?: Project
  portal?: Portal
}

interface FormQuestion {
  question: string,
  projects: Project[]
}

export interface QuestionAnswerControlRefProps {
  submitQuestion: (question: string) => void
}

const QuestionsAnswersControl = forwardRef<QuestionAnswerControlRefProps, Props>(function({
  project,
  portal,
}: Props, ref) {
  const { actionFetch } = useActionFetch()
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [firstCreatedOnTop, setFirstCreatedOnTop] = useState<boolean>(false)
  const showToast = useToaster()
  const [showOnlyMine, setShowOnlyMine] = useState(false)
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<FormQuestion>({
    defaultValues: {
      question: '',
      projects: project ? [project] : (portal?.projects ?? []),
    },
  })
  const { field: { ref: _ref, ...projectsField } } = useController({
    name: 'projects',
    control,
    rules: {
      validate: value =>
      project ? true : (Array.isArray(value) && value.length > 0)
    },
  })
  const orderedQuestionAnswers = [...questionAnswers].sort((a, b) => {
  const aTime = new Date(a.createdAt).getTime()
  const bTime = new Date(b.createdAt).getTime()

  return firstCreatedOnTop
    ? aTime - bTime
    : bTime - aTime
  })
    const {
    expandedItems,
    allExpanded: allQuestionsExpanded,
    toggleAll,
    onAccordionSelect,
  } = useAccordionExpand(orderedQuestionAnswers)

  const sessionToken = useMemo(() => getOrCreateSessionToken(), [])
  const sessionHeaders = useMemo(() => ({
    ...getRequestHeaders(),
    'X-QA-Session-Token': sessionToken,
  }), [sessionToken])

  
  const resourceQuery = useMemo(
  () => project
    ? { project_id: project.id }
    : { portal_slug: portal?.slug },
  [project, portal]
  )

  const route = useMemo(() => {
    const query = { ...resourceQuery, show_only_mine: showOnlyMine }
    return apiQuestionAnswers.index({ query })
  }, [resourceQuery, showOnlyMine])
    
  useEffect(() => {
    if (!project && !portal) return

    actionFetch<QuestionAnswer[]>(route, { headers: sessionHeaders })
      .then(data => setQuestionAnswers(data))
  }, [project, portal, route])

  const onSubmit = (formQuestion: FormQuestion & { projects?: Project[] }) => {
    setLoading(true)

    const vectorStoreIds = project
                           ? [project.vectorStoreId]
                           : formQuestion.projects?.map(({ vectorStoreId }) => vectorStoreId)

    const projectQuestionAnswersAttributes = project
                                             ? [{ project_id: project?.id }]
                                             : formQuestion
                                               .projects
                                               ?.map(({ id: projectId }) => ({ project_id: projectId }))

    actionFetch<QuestionAnswer>(apiQuestionAnswers.create({ query: resourceQuery }), {
      headers: sessionHeaders,
      body: JSON.stringify({
        question_answer: {
          question: formQuestion.question,
          vector_store_ids: vectorStoreIds,
          project_question_answers_attributes: projectQuestionAnswersAttributes,
        },
      }),
    }).then(qa => {
      trackQuestionSubmittedEvent(qa)
      setQuestionAnswers([...questionAnswers, qa])
      reset({
        question: '',
        projects: portal ? formQuestion.projects : [],
      })
    }).finally(() => setLoading(false))
  }

  const onTogglePublished = (qa: QuestionAnswer, isPublished: boolean) => {
    if (!portal) return

    actionFetch<QuestionAnswer>(apiQuestionAnswers.togglePublished(qa.id), {
      headers: sessionHeaders,
      body: JSON.stringify({ published: isPublished, portal_slug: portal.slug }),
    }).then((updated) => {
      showToast({ message: `Question is now visible ${isPublished ? 'to everyone' : 'only to you'}!` })
      setQuestionAnswers(prev => prev.map(item => item.id === qa.id ? updated : item))
    })
  }

  const renderActionButtons = () => (
    <div className="d-flex flex-shrink-0 gap-2">
      {questionAnswers.length > 1 &&
        <ElementWithTooltip
          tooltip={firstCreatedOnTop ? 'Show newest questions first' : 'Show oldest questions first'}
          placement="top"
        >
          <Button
            variant="primary"
            onClick={() => setFirstCreatedOnTop(a => !a)}
            aria-label={firstCreatedOnTop ? 'Sort newest first' : 'Sort oldest first'}
          >
            {firstCreatedOnTop
             ? <i className="fas fa-arrow-up" />
             : <i className="fas fa-arrow-down" />
            }
          </Button>
        </ElementWithTooltip>
      }
      {questionAnswers.length > 0 &&
        <AccordionToggleAllButton
          allExpanded={allQuestionsExpanded}
          singularLabel="questions"
          onToggle={toggleAll}
        />
      }
    </div>
  )

  useImperativeHandle(ref, () => {
    return {
      submitQuestion(question: string) {
        onSubmit({ question, projects: [] })
      },
    }
  })

  return (
    <div className="d-flex flex-column gap-3 mt-3">
      <Form
        onSubmit={handleSubmit(
          onSubmit,
          errors => console.log('FORM INVALID:', errors)
      )}
      className="d-flex flex-column gap-3"
      >
        <div className="d-flex align-items-stretch gap-2">
          <InputGroup className="flex-grow-1">
            <Form.Control
              placeholder="Ask a question"
              disabled={loading}
              {...register('question', { required: true })}
            />
            <LoadingButton loading={loading} text="Submit" type="submit" />
          </InputGroup>
          {!portal && renderActionButtons()}
        </div>

        {portal?.projects &&
          <>
            <div className="d-flex align-items-stretch gap-2">
              <div className="flex-grow-1">
                <TitledRecordsSelect<Project>
                  {...projectsField}
                  options={portal.projects}
                  isMulti
                  withAll
                />
              </div>
              {renderActionButtons()}
            </div>
            {errors.projects &&
              <Form.Text className="text-danger">Please select at least one project.</Form.Text>}
          </>
        }
      </Form>


      <div className="QuestionsAnswersControl d-flex flex-column gap-2 px-2 py-1">
        {portal &&
          <Form.Check
            checked={showOnlyMine}
            type="switch"
            id="custom-switch"
            label={showOnlyMine ? 'Show all' : 'Show mine'}
            onChange={e => setShowOnlyMine(e.target.checked)}
          />
        }

        <Accordion alwaysOpen activeKey={expandedItems} onSelect={onAccordionSelect}>
          {orderedQuestionAnswers.map((qa) => (
            <QuestionAnswerAccordionItem
              key={qa.id}
              qa={qa}
              portal={portal}
              onTogglePublished={onTogglePublished}
            />
          ))}
        </Accordion>
      </div>
    </div>
  )
})

export default QuestionsAnswersControl
